import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import {
  FaSearch,
  FaTimes,
  FaChevronDown,
  FaCircleNotch,
  FaCheckCircle,
  FaArrowRight,
  FaGem,
  FaSlidersH,
  FaBox,
} from "react-icons/fa";
import "./RetailerCatalogue.scss";

const generateOrderId = async () => {
  const date = new Date();
  const monthPrefix = `SV${date.getFullYear().toString().slice(-2)}${(date.getMonth() + 1).toString().padStart(2, "0")}`;
  const currentDay = date.getDate().toString().padStart(2, "0");
  const snapshot = await getDocs(collection(db, "partyOrders"));
  let highestSequence = 0;
  snapshot.docs.forEach((doc) => {
    const id = doc.data().orderId;
    if (id && id.startsWith(monthPrefix)) {
      const seq = parseInt(id.substring(8), 10);
      if (!isNaN(seq) && seq > highestSequence) highestSequence = seq;
    }
  });
  return `${monthPrefix}${currentDay}${(highestSequence + 1).toString().padStart(3, "0")}`;
};

const RetailerCatalogue = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filtering States
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [stockFilter, setStockFilter] = useState("All");
  const [categories, setCategories] = useState(["All"]);

  // Advanced Filter States
  const [showFilters, setShowFilters] = useState(false);
  const [minWeight, setMinWeight] = useState("");
  const [maxWeight, setMaxWeight] = useState("");
  const [subCategoryFilter, setSubCategoryFilter] = useState("");
  const [availableSubCategories, setAvailableSubCategories] = useState([]);

  // Bottom Sheet States
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [orderMode, setOrderMode] = useState("exact");
  const [orderForm, setOrderForm] = useState({
    qty: 1,
    weight: "",
    size: "",
    comments: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Swipe to Order
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const trackRef = useRef(null);

  const session = useMemo(() => {
    const stored = localStorage.getItem("retailerSession");
    return stored ? JSON.parse(stored) : { id: "GUEST", partyName: "Retailer" };
  }, []);

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const snapshot = await getDocs(collection(db, "products"));
        let prodList = [];
        let cats = new Set();

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.visibility === "Hidden") return;

          let normalizedStatus = data.stockStatus || "In Stock";
          if (normalizedStatus === "Ready Stock") normalizedStatus = "In Stock";
          if (normalizedStatus === "Catalog")
            normalizedStatus = "Made to Order";

          if (data.category) cats.add(data.category);

          prodList.push({
            id: doc.id,
            ...data,
            visibility: "Public",
            stockStatus: normalizedStatus,
          });
        });

        setCategories(["All", ...Array.from(cats)]);
        prodList.sort((a, b) => {
          const timeA = a.createdAt?.seconds || a.createdAt || 0;
          const timeB = b.createdAt?.seconds || b.createdAt || 0;
          return timeB - timeA;
        });

        setProducts(prodList);
        setFilteredProducts(prodList);
      } catch (error) {
        console.error("Error fetching catalog:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCatalog();
  }, []);

  useEffect(() => {
    if (activeCategory === "All") {
      const subs = new Set(products.map((p) => p.subCategory).filter(Boolean));
      setAvailableSubCategories(Array.from(subs));
    } else {
      const categoryProducts = products.filter(
        (p) => p.category === activeCategory,
      );
      const subs = new Set(
        categoryProducts.map((p) => p.subCategory).filter(Boolean),
      );
      setAvailableSubCategories(Array.from(subs));
    }
    setSubCategoryFilter("");
  }, [activeCategory, products]);

  useEffect(() => {
    let temp = [...products];
    if (activeCategory !== "All")
      temp = temp.filter((p) => p.category === activeCategory);
    if (stockFilter !== "All") {
      if (stockFilter === "In Stock")
        temp = temp.filter((p) => p.stockStatus === "In Stock");
      if (stockFilter === "Catalog")
        temp = temp.filter(
          (p) =>
            p.stockStatus === "Made to Order" || p.stockStatus === "Sold Out",
        );
    }
    if (search) {
      const s = search.toLowerCase();
      temp = temp.filter(
        (p) =>
          p.searchIndex?.includes(s) ||
          p.sku?.toLowerCase().includes(s) ||
          p.name?.toLowerCase().includes(s),
      );
    }
    if (subCategoryFilter)
      temp = temp.filter((p) => p.subCategory === subCategoryFilter);
    if (minWeight)
      temp = temp.filter((p) => parseFloat(p.weight) >= parseFloat(minWeight));
    if (maxWeight)
      temp = temp.filter((p) => parseFloat(p.weight) <= parseFloat(maxWeight));

    setFilteredProducts(temp);
  }, [
    search,
    activeCategory,
    stockFilter,
    subCategoryFilter,
    minWeight,
    maxWeight,
    products,
  ]);

  const clearAdvancedFilters = () => {
    setMinWeight("");
    setMaxWeight("");
    setSubCategoryFilter("");
    setShowFilters(false);
  };

  const openProduct = (product) => {
    setSelectedProduct(product);
    setOrderMode("exact");
    setOrderForm({
      qty: 1,
      weight: product.weight || "",
      size: product.size || "",
      comments: "",
    });
    document.body.style.overflow = "hidden";
  };

  const closeProduct = () => {
    setIsClosing(true);
    setTimeout(() => {
      setSelectedProduct(null);
      setIsClosing(false);
      setIsSuccess(false);
      setSwipeProgress(0);
      document.body.style.overflow = "auto";
    }, 300);
  };

  const handleTouchStart = () => {
    if (!isSubmitting && !isSuccess) setIsSwiping(true);
  };
  const handleTouchMove = (e) => {
    if (!isSwiping || isSubmitting || isSuccess) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const track = trackRef.current.getBoundingClientRect();
    const maxSwipe = track.width - 56;
    let newProgress = clientX - track.left - 28;
    if (newProgress < 0) newProgress = 0;
    if (newProgress > maxSwipe) newProgress = maxSwipe;
    setSwipeProgress(newProgress);
    if (newProgress >= maxSwipe * 0.95) {
      setIsSwiping(false);
      setSwipeProgress(maxSwipe);
      handlePlaceOrder();
    }
  };
  const handleTouchEnd = () => {
    if (isSwiping) {
      setIsSwiping(false);
      setSwipeProgress(0);
    }
  };

  const handlePlaceOrder = async () => {
    setIsSubmitting(true);
    try {
      const orderId = await generateOrderId();
      const isCasting = ["Ring", "Earrings", "Pendant"].includes(
        selectedProduct.category,
      );
      const isCustom = orderMode === "custom";

      let docData = {
        orderId,
        partyId: session.id,
        deliveryDate: "TBD",
        comments: isCustom
          ? orderForm.comments
          : "Ordered exactly as shown in catalog.",
        status: "Pending",
        totalQuantity: parseInt(orderForm.qty) || 1,
        createdAt: serverTimestamp(),
        unseenCount: 1,
        catalogRefId: selectedProduct.id,
        orderType: isCasting ? "Casting" : "Single",
      };

      if (isCasting) {
        docData.files = selectedProduct.images || [];
        docData.castingDetails = {
          item: selectedProduct.name,
          carat: selectedProduct.carat || "22K",
          weight: isCustom ? orderForm.weight : selectedProduct.weight,
          size: isCustom ? orderForm.size : selectedProduct.size,
          stone: "As per image",
          budget: "",
        };
      } else {
        docData.product = selectedProduct.name;
        docData.singleItems = [
          {
            weight: isCustom ? orderForm.weight : selectedProduct.weight,
            length: isCustom ? orderForm.size : selectedProduct.size,
            lengthUnit: "Standard",
            quantity: parseInt(orderForm.qty) || 1,
            itemStatus: "Pending",
            fileUrls: selectedProduct.images || [],
          },
        ];
      }

      await addDoc(collection(db, "partyOrders"), docData);
      setIsSuccess(true);
      setTimeout(() => closeProduct(), 2500);
    } catch (error) {
      alert("Failed to place order.");
      setIsSubmitting(false);
      setSwipeProgress(0);
    }
  };

  const getStatusBadgeClass = (status) => {
    if (status === "In Stock") return "in-stock";
    if (status === "Sold Out") return "sold-out";
    return "mto";
  };

  return (
    <div className="v-rt-cat-wrapper fade-in">
      {/* 1. LUXURY LOOKBOOK HEADER */}
      <nav className="v-rt-cat-nav">
        <div className="v-rt-cat-nav-top">
          <h2 className="brand-title">Collection</h2>
          <div className="nav-actions">
            <button
              className={`nav-icon-btn ${showFilters ? "active" : ""}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaSlidersH />
              {(minWeight || maxWeight || subCategoryFilter) && (
                <span className="active-dot"></span>
              )}
            </button>
          </div>
        </div>

        <div className="v-rt-cat-search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name or SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="v-rt-cat-category-scroll">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`cat-pill ${activeCategory === cat ? "active" : ""}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="v-rt-cat-stock-toggles">
          <button
            className={`toggle-btn ${stockFilter === "All" ? "active" : ""}`}
            onClick={() => setStockFilter("All")}
          >
            All Designs
          </button>
          <button
            className={`toggle-btn ${stockFilter === "In Stock" ? "active" : ""}`}
            onClick={() => setStockFilter("In Stock")}
          >
            Ready Stock
          </button>
          <button
            className={`toggle-btn ${stockFilter === "Catalog" ? "active" : ""}`}
            onClick={() => setStockFilter("Catalog")}
          >
            Made to Order
          </button>
        </div>

        {/* ELEGANT ADVANCED FILTERS */}
        <div
          className={`v-rt-cat-advanced-filters ${showFilters ? "open" : ""}`}
        >
          <div className="filter-inner">
            <div className="filter-block">
              <label>Weight Range</label>
              <div className="weight-inputs">
                <input
                  type="number"
                  placeholder="Min (g)"
                  value={minWeight}
                  onChange={(e) => setMinWeight(e.target.value)}
                />
                <span className="divider">-</span>
                <input
                  type="number"
                  placeholder="Max (g)"
                  value={maxWeight}
                  onChange={(e) => setMaxWeight(e.target.value)}
                />
              </div>
            </div>

            {availableSubCategories.length > 0 && (
              <div className="filter-block">
                <label>Design Type</label>
                <select
                  value={subCategoryFilter}
                  onChange={(e) => setSubCategoryFilter(e.target.value)}
                >
                  <option value="">All Types</option>
                  {availableSubCategories.map((sub) => (
                    <option key={sub} value={sub}>
                      {sub}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="filter-actions">
              <button className="btn-clear" onClick={clearAdvancedFilters}>
                Clear
              </button>
              <button
                className="btn-apply"
                onClick={() => setShowFilters(false)}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* 2. THE LOOKBOOK GRID */}
      <div className="v-rt-cat-grid-body">
        {isLoading ? (
          <div className="v-rt-cat-loader">
            <FaCircleNotch className="spin" /> Loading Vault...
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="v-rt-cat-empty">
            <FaGem className="empty-icon" />{" "}
            <p>No designs match your filters.</p>
          </div>
        ) : (
          <div className="v-rt-cat-lookbook-grid">
            {filteredProducts.map((p) => (
              <div
                key={p.id}
                className={`v-rt-cat-item ${p.stockStatus === "Sold Out" ? "dimmed" : ""}`}
                onClick={() => openProduct(p)}
              >
                <div className="item-image-box">
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt={p.name} loading="lazy" />
                  ) : (
                    <div className="no-img">
                      <FaGem />
                    </div>
                  )}
                  <span
                    className={`item-badge ${getStatusBadgeClass(p.stockStatus)}`}
                  >
                    {p.stockStatus === "In Stock"
                      ? "Ready"
                      : p.stockStatus === "Sold Out"
                        ? "Sold Out"
                        : "Order"}
                  </span>
                </div>
                <div className="item-details">
                  <h4 className="item-title">{p.name}</h4>
                  <div className="item-meta">
                    <span className="item-weight">{p.weight}g</span>
                    {p.sku && <span className="item-sku">{p.sku}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 3. THE "PEEK & BUY" BOTTOM SHEET OVERLAY (UNCHANGED FUNCTIONALITY) */}
      {selectedProduct && (
        <div className={`v-rt-cat-overlay ${isClosing ? "closing" : ""}`}>
          <div className="v-rt-cat-backdrop" onClick={closeProduct}></div>

          <div className={`v-rt-cat-sheet ${isClosing ? "closing" : ""}`}>
            {isSuccess ? (
              <div className="v-rt-cat-success-state pop-in">
                <FaCheckCircle className="check-icon" />
                <h3>Order Confirmed!</h3>
                <p>Sent to factory for processing.</p>
              </div>
            ) : (
              <>
                <div className="sheet-header">
                  <div className="pull-bar"></div>
                  <button className="close-btn" onClick={closeProduct}>
                    <FaTimes />
                  </button>
                </div>

                <div className="sheet-scroll-body">
                  <div className="sheet-hero-img">
                    <img
                      src={selectedProduct.images?.[0]}
                      alt="Product"
                      className="bg-blur"
                    />
                    <img
                      src={selectedProduct.images?.[0]}
                      alt="Product"
                      className="fg-crisp"
                    />
                  </div>

                  <div className="sheet-title-row">
                    <h2>{selectedProduct.name}</h2>
                    <span
                      className={`status-badge ${getStatusBadgeClass(selectedProduct.stockStatus)}`}
                    >
                      {selectedProduct.stockStatus}
                    </span>
                  </div>

                  <div className="sheet-specs-row">
                    <div className="spec">
                      <label>Weight</label>
                      <span>{selectedProduct.weight || "TBD"}g</span>
                    </div>
                    <div className="spec">
                      <label>Size</label>
                      <span>{selectedProduct.size || "N/A"}</span>
                    </div>
                    <div className="spec">
                      <label>Purity</label>
                      <span>{selectedProduct.carat || "22K"}</span>
                    </div>
                    <div className="spec">
                      <label>SKU</label>
                      <span className="sku-font">
                        {selectedProduct.sku || "TBD"}
                      </span>
                    </div>
                  </div>

                  {selectedProduct.description && (
                    <div className="sheet-description-box">
                      <p>{selectedProduct.description}</p>
                    </div>
                  )}

                  <div className="sheet-order-section">
                    <h3>How would you like to order?</h3>
                    <div className="order-mode-toggle">
                      <button
                        className={orderMode === "exact" ? "active" : ""}
                        onClick={() => setOrderMode("exact")}
                      >
                        Order Exact Design
                      </button>
                      <button
                        className={orderMode === "custom" ? "active" : ""}
                        onClick={() => setOrderMode("custom")}
                      >
                        Request Modification
                      </button>
                    </div>

                    {orderMode === "custom" && (
                      <div className="custom-inputs pop-in">
                        <div className="input-row">
                          <div className="input-group">
                            <label>Required Weight (g)</label>
                            <input
                              type="number"
                              value={orderForm.weight}
                              onChange={(e) =>
                                setOrderForm({
                                  ...orderForm,
                                  weight: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="input-group">
                            <label>Required Size</label>
                            <input
                              type="text"
                              value={orderForm.size}
                              onChange={(e) =>
                                setOrderForm({
                                  ...orderForm,
                                  size: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="input-group">
                          <label>Modification Instructions</label>
                          <textarea
                            rows="2"
                            placeholder="e.g. Change chain to 22K and add red stones..."
                            value={orderForm.comments}
                            onChange={(e) =>
                              setOrderForm({
                                ...orderForm,
                                comments: e.target.value,
                              })
                            }
                          ></textarea>
                        </div>
                      </div>
                    )}

                    <div className="input-group qty-group">
                      <label>Total Quantity Needed</label>
                      <div className="qty-stepper">
                        <button
                          onClick={() =>
                            setOrderForm({
                              ...orderForm,
                              qty: Math.max(1, orderForm.qty - 1),
                            })
                          }
                        >
                          -
                        </button>
                        <input type="number" value={orderForm.qty} readOnly />
                        <button
                          onClick={() =>
                            setOrderForm({
                              ...orderForm,
                              qty: orderForm.qty + 1,
                            })
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sheet-bottom-bar">
                  {isSubmitting ? (
                    <div className="submitting-state">
                      <FaCircleNotch className="spin" /> Sending to Vault...
                    </div>
                  ) : (
                    <div
                      className="swipe-track"
                      ref={trackRef}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      onMouseMove={handleTouchMove}
                      onMouseUp={handleTouchEnd}
                      onMouseLeave={handleTouchEnd}
                    >
                      <div
                        className="swipe-fill"
                        style={{ width: `${swipeProgress + 28}px` }}
                      ></div>
                      <span className="swipe-text">Swipe to Place Order</span>
                      <div
                        className="swipe-thumb"
                        style={{ transform: `translateX(${swipeProgress}px)` }}
                        onTouchStart={handleTouchStart}
                        onMouseDown={handleTouchStart}
                      >
                        <FaArrowRight className="thumb-icon" />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RetailerCatalogue;
